import { Field, useFormikContext } from 'formik';
import React, { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import ControllerImage from '../../images/Controller.svg';
import { ListQuery } from '../../models/ListQuery';
import { getDeviceProfileNames } from '../../services/hardwarePresetService';
import { IAppState } from '../../store/types';
import { appendFilter } from '../../utilities/filters';
import { validateItemName } from '../../utilities/Validation';
import EntityHierarchy from '../entityHierarchy/entityHierarchy';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';
import Togglebox from '../togglebox/togglebox';
import { PRESET_TYPES } from '../../constants/preset-constants';

interface MasterControllerCardProps {
    setFormikContext: (formikContext) => void
}

const MasterControllerCard: FC<MasterControllerCardProps> = ({ setFormikContext }) => {
    const formikContext = useFormikContext<any>();
    const { translate } = useLocale();
    const siteId = useSelector((state: IAppState) => state.site.site.value)

    useEffect(() => {
        setFormikContext(formikContext)
    }, [])

    return <>
        <div className="w-full p-2 inline-flex justify-start items-center bg-white">
            <span>
                <img className='hardware-image' src={ControllerImage} alt="Controller" />
            </span>
            <div className="flex flex-col py-4 px-4 card-details">
                {formikContext.values.id ?
                    <div className="flex flex-col px-4">
                        <EntityHierarchy title={formikContext.values.name} tree={{ label: formikContext.values.site, node: { label: formikContext.values.name } }} />
                    </div>
                    : null}
            </div>
            <div className="flex flex-col py-4 px-8">
                <div className="mb-2">
                    <div className="flex flex-row justify-between">
                        <Label
                            required
                            htmlFor="name"
                            className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.name)}:</Label>
                        {formikContext.errors.name ? <span className='text-red-600 text-xs'>{formikContext.errors.name}</span> : null}
                    </div>
                    <Field
                        type="text"
                        name="name"
                        autoFocus
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={(value) => validateItemName(value, translate)} />
                </div>

                <div className="mb-2">
                    <div className="flex flex-row justify-between">
                        <Label
                            htmlFor="fixedAddress"
                            className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.fixedAddress)}:</Label>
                    </div>
                    <Field
                        name="fixedAddress"
                        disabled={true}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
                </div>

                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="sla"
                        className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.logicalAddress)}:</Label>
                </div>
                <Field
                    name="sla"
                    disabled={true}
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
            </div>
            <div className="flex flex-col py-4 px-8">
                <div className="mb-2">
                    <div className="flex flex-row justify-between">
                        <Label
                            required
                            htmlFor="presetId"
                            className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.preset)}:</Label>
                        {formikContext.errors.peresetId ? <span className='text-red-600 text-xs'>{formikContext.errors.peresetId}</span> : null}
                    </div>
                    <PaginatedSearchableSelectField
                        name={translate.byKey(TranslationKeys.preset)}
                        key={"presetId"}
                        accessor='presetId'
                        dataMethod={(listParameters?: ListQuery) => getDeviceProfileNames(PRESET_TYPES.CONTROLLER, { ...listParameters, filters: appendFilter(formikContext.values.siteId ?? siteId, 'Site id', 'site.id', listParameters?.filters ?? []) })}
                        searchAccessor={'trnName'}
                        onSelect={(event, item?) => {
                            formikContext.setFieldValue('preset', item?.label)
                            formikContext.setFieldValue('presetId', item?.value);
                        }}
                        selectedItemLabel={formikContext.values.preset} />
                </div>
                <div className="mb-2">
                    <div className="flex flex-row justify-between">
                        <Label
                            htmlFor="macAddress"
                            className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.macAddress)}:</Label>
                    </div>
                    <Field
                        name="macAddress"
                        disabled={true}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
                </div>
                <div className="flex flex-row justify-between">
                    <Label
                        htmlFor="enabled"
                        className="text-xs text-gray-600 mt-1 my-1">{translate.byKey(TranslationKeys.enabled)}:</Label>
                </div>
                <div className="my-0">
                    <Togglebox name="enabled" label="" />
                </div>
            </div>
        </div>
    </>;
};

export default MasterControllerCard;
